"use client";

import React, { useEffect, useRef, useState } from "react";
import { Focus, Palette, Grid3x3 } from 'lucide-react';
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const blueprintFeatures = [{
  label: "01. LIDERANÇA",
  title: "Visão Estratégica",
  description: "Desenvolvimento de competências críticas para guiar organizações em cenários de alta complexidade e transformação constante.",
  Icon: Focus
}, {
  label: "02. NETWORKING",
  title: "Capital Relacional",
  description: "Acesso a um ecossistema exclusivo de líderes e conselheiros, potencializando oportunidades de alto impacto no mercado.",
  Icon: Palette
}, {
  label: "03. LONGEVIDADE",
  title: "Carreira Sustentável",
  description: "Planejamento de transição e continuidade profissional para executivos que buscam relevância em todas as fases da vida.",
  Icon: Grid3x3
}];
export default function FromLensToLayout() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222897" id="masterproecosystem" className="relative bg-background text-foreground py-32 px-6 md:px-12 lg:px-24 overflow-hidden">
            {}
            <div className="absolute inset-0 bg-primary/[0.03] pointer-events-none" aria-hidden="true" />

            <div ref={ref} className={`relative max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-12 lg:gap-20 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <h2 className="col-span-full font-serif font-bold text-3xl md:text-4xl lg:text-5xl mb-4 tracking-tight text-primary">
                    Os Pilares da MasterPro+
                </h2>

                {blueprintFeatures.map((feature, i) => <div key={i} data-index={i} className="flex flex-col items-start group" style={{
        transitionDelay: `${i * 200}ms`
      }}>
                        <feature.Icon className="size-12 text-primary/60 stroke-[1px] mb-8 transition-transform duration-700 group-hover:scale-110 group-hover:text-primary" aria-hidden="true" />

                        <span className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4 block">
                            {feature.label}
                        </span>

                        <h3 className="font-serif text-2xl font-semibold mb-4 text-foreground tracking-tight">
                            {feature.title}
                        </h3>

                        <p className="font-serif text-base md:text-lg text-muted-foreground leading-relaxed italic">
                            {feature.description}
                        </p>
                    </div>)}
            </div>
        </section>;
}